import {
  calculateAssetBookValueAtDate,
  calculateAssetPurchaseCashFlow,
  resolveActiveAssetEvents,
} from './assets.js';
import type { AssetEventInput } from './types.js';
import { startOfMonthUTC, toDate } from './dates.js';

export interface FundingEventInput {
  type?: string;
  date: Date | string;
  amount?: number;
}

/** Sum of funding round events in a calendar month (excludes initialBalance — that is applied separately). */
export function sumFundingEventsInMonth(
  events: FundingEventInput[],
  targetMonth: Date
): number {
  const target = startOfMonthUTC(targetMonth).getTime();
  return events
    .filter((event) => {
      if (event.type !== 'funding' || !event.amount) return false;
      const eventDate = toDate(event.date);
      if (!eventDate) return false;
      return startOfMonthUTC(eventDate).getTime() === target;
    })
    .reduce((sum, event) => sum + (event.amount ?? 0), 0);
}

export function sumAssetPurchasesInMonth(
  events: Array<{ type?: string; date: Date | string; amount?: number }>,
  targetMonth: Date
): number {
  return events
    .filter((event) => event.type === 'asset')
    .reduce((sum, asset) => sum + calculateAssetPurchaseCashFlow(asset, targetMonth), 0);
}

/** Whether a month is on or after the startup's official start month */
export function isOnOrAfterStartupStart(startupStartDate: Date, month: Date): boolean {
  return startOfMonthUTC(month).getTime() >= startOfMonthUTC(startupStartDate).getTime();
}

export function isStartupStartMonth(startupStartDate: Date, month: Date): boolean {
  return startOfMonthUTC(month).getTime() === startOfMonthUTC(startupStartDate).getTime();
}

/** Sum of liquid asset book values owned on or before targetDate (adds to available cash). */
export function sumLiquidAssetBookValue(
  events: Array<AssetEventInput & { type?: string; id?: string }>,
  targetDate: Date,
): number {
  const activeAssets = resolveActiveAssetEvents(events, targetDate);
  return activeAssets.reduce(
    (sum, asset) => sum + calculateAssetBookValueAtDate(asset, targetDate),
    0,
  );
}
